import React, { useState, useEffect } from 'react';
import { Table as TableIcon, X, FileText, LayoutList } from 'lucide-react';
import { cn } from '../../lib/utils';
import { datasetApi } from '../../api/datasetApi';
import { showErrorToast, showSuccessToast } from '../../lib/toast';
import SchemaTab from './DatasetExplorer/SchemaTab';
import DetailsTab from './DatasetExplorer/DetailsTab';
import TableDescription from './DatasetExplorer/TableDescription';
import DatasetContextBar from './DatasetExplorer/DatasetContextBar';

const PreviewTable = ({ dataset, onClose, onMetadataUpdate }) => {
  const [activeTab, setActiveTab] = useState('description');
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!dataset) return;

    const fetchMetadata = async () => {
      setIsLoading(true);
      try {
        const data = await datasetApi.getDatasetMetadata(dataset.id);
        setMetadata(data);
      } catch (err) {
        console.error('Failed to fetch dataset metadata:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetadata();
  }, [dataset]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!dataset) return null;

  const handleUpdateMetadata = async (updatedData) => {
    try {
      const result = await datasetApi.updateDatasetMetadata(dataset.id, updatedData, {
        suppressGlobalErrorToast: true,
      });
      setMetadata(prev => ({ ...prev, ...result }));
      onMetadataUpdate?.();
      showSuccessToast('Metadata saved.');
    } catch (err) {
      console.error('Failed to update metadata:', err);
      showErrorToast('Could not save metadata. Please try again.');
    }
  };

  const displayName = dataset.type === 'bigquery' && dataset.name.includes('.')
    ? dataset.name.split('.').pop()
    : dataset.name;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-6">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-[40px] shadow-2xl w-full max-w-7xl h-full max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200 overflow-hidden transition-colors">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between shrink-0 bg-white dark:bg-gray-950 transition-colors">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl shrink-0">
              <TableIcon size={20} />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate" title={dataset.name}>
                {displayName}
              </h3>
            </div>
            <div
              role="tablist"
              aria-label="Dataset details sections"
              className="inline-flex w-fit shrink-0 flex-wrap items-center gap-1 rounded-[22px] border border-gray-200/70 bg-white/70 p-1.5 shadow-inner shadow-gray-200/60 backdrop-blur dark:border-gray-800 dark:bg-gray-900/60 dark:shadow-black/20"
            >
              {[
                { id: 'description', label: 'Description', icon: FileText },
                { id: 'details', label: 'Details', icon: LayoutList },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'group inline-flex items-center gap-2 rounded-2xl px-4 py-2 font-black text-[10px] uppercase tracking-widest transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40',
                    activeTab === tab.id
                      ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-300 dark:bg-gray-800 dark:text-emerald-300 dark:ring-emerald-700/70'
                      : 'text-gray-500 hover:bg-gray-100/80 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/80 dark:hover:text-white'
                  )}
                >
                  <tab.icon size={14} className="shrink-0" strokeWidth={2.5} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-2xl transition-all text-gray-400 hover:text-gray-900 dark:hover:text-white active:scale-90 shrink-0 ml-4"
          >
            <X size={24} />
          </button>
        </div>
        <div key={activeTab} className="flex-1 min-h-0 overflow-y-auto custom-scrollbar bg-gray-50/30 dark:bg-gray-900/10">
          {isLoading ? (
            <div className="min-h-[240px] flex items-center justify-center p-6">
              <div className="flex flex-col items-center gap-4">
                <div className="w-8 h-8 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
                <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Hydrating Source Data...</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4 p-6">
              <DatasetContextBar
                dataset={dataset}
                metadata={metadata}
                onNavigate={onClose}
              />
              <div className="bg-white dark:bg-gray-950 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm transition-colors">
                {activeTab === 'description' && (
                  <>
                    <div className="border-b border-gray-100 p-5 dark:border-gray-800">
                      <TableDescription
                        summary={metadata?.summary}
                        isFullScreen
                        compact
                        onUpdateMetadata={handleUpdateMetadata}
                      />
                    </div>
                    <SchemaTab
                      schema={metadata?.schema}
                      summary={metadata?.summary}
                      isFullScreen
                      compact
                      summaryOutside
                      onUpdateMetadata={handleUpdateMetadata}
                      spatialColumns={metadata?.spatial_columns}
                      temporalColumns={metadata?.temporal_columns}
                    />
                  </>
                )}
                {activeTab === 'details' && (
                  <DetailsTab
                    dataset={{ ...dataset, ...metadata }}
                    samples={metadata?.samples}
                    embedded
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewTable;
